import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase }                                           from '@/shared/lib/supabase';
import { obterOuCriarSessionId }                              from '@/shared/lib/jukeboxSession';
import type { FutebolVotacaoJogo }                           from '@/shared/types';
import { votacaoRepository }                                  from '../../data/votacaoRepository';
import { atualizarIdsUltimoCiclo } from '../../domain/usecases/selecionarCandidatos';
import { derivarCandidatos, ordenarPorVotos } from '../../domain/usecases/derivarCandidatos';
import { futebolRepository }                                  from '@/features/futebol';

export interface VotacaoCandidatosState {
  jogos:           FutebolVotacaoJogo[];
  loading:         boolean;
  votado:          number | null;
  votando:         boolean;
  vencedor:        FutebolVotacaoJogo | null;
  fixtureIdAtivo:  number | null;
  totalVotos:      number;
  lider:           FutebolVotacaoJogo | null;
  /** 2+ candidatos ativos — votação aberta para escolha do próximo jogo. */
  votacaoAberta:   boolean;
  votar:           (fixtureId: number) => Promise<void>;
}

// Gera uma chave de localStorage baseada nos fixture_ids dos jogos da votação atual.
// Assim cada sessão de votação é independente, mesmo que ocorra no mesmo dia.
function gerarChaveVotacao(jogos: FutebolVotacaoJogo[]): string | null {
  if (jogos.length === 0) return null;
  const ids = jogos.map((j) => j.fixture_id).sort((a, b) => a - b).join('-');
  return `futebol_voto_sessao_${ids}`;
}

// Remove chaves antigas de votações anteriores do localStorage.
function limparChavesAntigas(chaveAtual: string | null): void {
  const prefixo = 'futebol_voto_';
  const keysParaRemover: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key && key.startsWith(prefixo) && key !== chaveAtual) {
      keysParaRemover.push(key);
    }
  }
  keysParaRemover.forEach((k) => localStorage.removeItem(k));
}

export function useVotacaoCandidatos(telaId: string): VotacaoCandidatosState {
  const [jogos,          setJogos]          = useState<FutebolVotacaoJogo[]>([]);
  const [loading,        setLoading]        = useState(true);
  const [votado,         setVotado]         = useState<number | null>(null);
  const [votando,        setVotando]        = useState(false);
  const [fixtureIdAtivo, setFixtureIdAtivo] = useState<number | null>(null);
  const [tempoJogoAtivo, setTempoJogoAtivo] = useState<string | null>(null);

  // Ref do setter — mesmo padrão do useTvVotacao — evita closure stale no callback do realtime
  const setJogosRef = useRef(setJogos);
  useEffect(() => { setJogosRef.current = setJogos; });

  // Memoriza o conjunto de fixture_ids do último ciclo com candidatos ativos —
  // identifica qual vencedor acumulado pertence ao ciclo que acabou de fechar
  // (ver selecionarVencedorAtivo).
  const ultimoCicloIdsRef = useRef<number[]>([]);
  const { ativos, totalVotos, lider, vencedor: vencedorCandidato } = useMemo(
    () => derivarCandidatos(jogos, ultimoCicloIdsRef.current),
    [jogos],
  );
  useEffect(() => {
    ultimoCicloIdsRef.current = atualizarIdsUltimoCiclo(ultimoCicloIdsRef.current, ativos.map((j) => j.fixture_id));
  }, [ativos]);

  // Chave única por sessão de votação (baseada nos fixture_ids dos candidatos ativos)
  const storageKey = useMemo(() => gerarChaveVotacao(ativos), [ativos]);

  // Sempre que a chave mudar (nova votação iniciada), verifica se já votou
  useEffect(() => {
    if (!storageKey) {
      setVotado(null);
      return;
    }
    const salvo = localStorage.getItem(storageKey);
    // Valida contra NaN: localStorage corrompido/adulterado não deve virar um
    // fixture_id inválido que passa nos checks `!== null` adiante.
    const parsed = salvo !== null ? Number(salvo) : null;
    setVotado(parsed !== null && Number.isFinite(parsed) ? parsed : null);
    // Limpa chaves de votações anteriores
    limparChavesAntigas(storageKey);
  }, [storageKey]);

  const recarregar = useCallback(() => {
    if (!telaId) return;
    votacaoRepository
      .buscarVotacaoHoje(telaId)
      .then(setJogos)
      .catch(() => {});
  }, [telaId]);

  useEffect(() => {
    if (!telaId) return;
    votacaoRepository
      .buscarVotacaoHoje(telaId)
      .then(setJogos)
      .catch(() => {})
      .finally(() => setLoading(false));

    // Realtime: mesmo padrão do useTvVotacao
    //  - UPDATE → atualiza o jogo no estado local (sem re-fetch) — cobre votos em tempo real
    //  - INSERT/DELETE → re-fetch completo (nova votação ou encerramento)
    const channel = supabase
      .channel(`futebol-votacao-mobile-${telaId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'futebol_votacao_jogos', filter: `tela_id=eq.${telaId}` },
        (payload) => {
          if (payload.eventType === 'UPDATE') {
            const updated = payload.new as FutebolVotacaoJogo;
            setJogosRef.current((prev) =>
              ordenarPorVotos(prev.map((j) => (j.id === updated.id ? updated : j))),
            );
          } else {
            // INSERT ou DELETE: re-fetch completo para refletir nova votação
            recarregar();
          }
        },
      )
      .subscribe();

    // Re-fetch ao retornar ao primeiro plano: cobre o caso em que o ciclo de votação
    // foi aberto enquanto o Jukebox estava em background e o evento Realtime foi perdido.
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') recarregar();
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      supabase.removeChannel(channel);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [recarregar]);

  // Monitora futebol_partida_ativa (DESTA tela) para detectar quando o jogo vencedor começa
  useEffect(() => {
    if (!telaId) return;
    futebolRepository.buscarPartidaAtiva(telaId)
      .then(p => {
        setFixtureIdAtivo(p?.fixture_id ?? null);
        setTempoJogoAtivo(p?.tempo_jogo ?? null);
      })
      .catch(() => {});

    const ch = supabase
      .channel(`futebol-partida-ativa-votacao-${telaId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'futebol_partida_ativa', filter: `tela_id=eq.${telaId}` },
        (payload) => {
          const p = payload.new as { fixture_id: number | null; tempo_jogo: string | null };
          setFixtureIdAtivo(p.fixture_id ?? null);
          setTempoJogoAtivo(p.tempo_jogo ?? null);
        },
      )
      .subscribe();

    return () => { supabase.removeChannel(ch); };
  }, [telaId]);

  const votar = useCallback(async (fixtureId: number) => {
    if (votado !== null || votando || !storageKey) return;
    setVotando(true);
    try {
      const sessionId = obterOuCriarSessionId(telaId);
      await votacaoRepository.votar(fixtureId, telaId, sessionId);
      localStorage.setItem(storageKey, String(fixtureId));
      setVotado(fixtureId);
    } catch (err) {
      // Servidor recusou por duplo voto: sincroniza o estado local
      if (err instanceof Error && err.message === 'ja_votou') {
        const salvo = storageKey ? localStorage.getItem(storageKey) : null;
        const parsed = salvo !== null ? Number(salvo) : NaN;
        if (Number.isFinite(parsed)) {
          setVotado(parsed);
        } else if (ativos.length === 1) {
          localStorage.setItem(storageKey, String(ativos[0].fixture_id));
          setVotado(ativos[0].fixture_id);
        } else {
          setVotado(-1);
        }
      }
      // 'votacao_fechada' e outros erros: silencioso (UI permanece disponível)
    } finally {
      setVotando(false);
    }
  }, [votado, votando, storageKey, telaId, ativos]);

  const votacaoAberta = ativos.length >= 2;

  // Anuncia o vencedor só quando não há candidatos ativos e o jogo ainda não encerrou.
  // Após o jogo terminar, pollAtivo zera fixture_id mas tempo_jogo permanece 'FT' no banco —
  // esse sinal evita que AnuncioVencedor reapareça a cada mount do Jukebox pós-encerramento.
  const jogoVencedorEncerrou = fixtureIdAtivo === null && tempoJogoAtivo === 'FT';
  const vencedor = ativos.length === 0 && !jogoVencedorEncerrou
    ? vencedorCandidato
    : null;

  return { jogos: ativos, loading, votado, votando, vencedor, fixtureIdAtivo, totalVotos, lider, votacaoAberta, votar };
}
